import React, { useEffect, useMemo, useState } from 'react'
import {
    Tab,
    Card,
    Form,
    Tabs,
    Button,
    Spinner,
    Divider,
    NavButton,
    SaveButton,
    ResetButton,
    DeleteButton,
    CardWithBreadcrumb, 
    Position
} from '../../../Elements'

import { FieldArray } from 'formik'
import { Icon, Tooltip, NonIdealState } from '@blueprintjs/core'

import { useLocation } from 'react-router-dom'
import JobInfoCard from './JobInfoCard'
import JobDetailForm from './JobDetailForm'
import { Prompt } from 'react-router-dom'
import { isChanged } from './helpers'

import { DEFAULT_JOB_DETAIL_STATE } from '../../../utils/state'
import { CheckAddressDialog } from '../../CheckAddressDialog'
import { useMediaQuery } from 'react-responsive'
import {CanDeleteJob, CanSendBuilderEmail} from '../../Auth'
import DocumentTree from '../../DocumentTree'
import DeleteJobDialog from './DeleteJobDialog'
import BuilderEmail from "../../BuilderEmail";
import {useHasRight} from "../../../Hooks/hasRight";
import {CAN_VIEW_JOB} from "../../../utils/enums";
import { CriticalNoteDialog, convertJobDetailToCriticalNoteData } from '../../CriticalNoteDialog'

const addButtonStyle = {
    marginRight: '10px'
}

const DEFAULT_SUB_TAB = 'sub_jobs-0'

const _getTabTitle = (sub_job) => {
    if (sub_job && sub_job.job_category) {
        let job_types = sub_job.job_types &&
            sub_job.job_types.map(j => j.name).join(', ')

        let job_category = `(${sub_job.job_category.name})`

        const isPebble = sub_job.job_category.id === 2
        const isGunite = sub_job.job_category.id === 1

        const isPresite = sub_job.presite && sub_job.presite.checkin_date

        let date = (isGunite && sub_job.gunite_details && sub_job.gunite_details.finished_date)
            || (isPebble && sub_job.pebble_details && sub_job.pebble_details.app_date)

        return <span>
            {isPresite && <Icon icon='tick'
                iconSize={15}
                htmlTitle='Pre-Site Complete'
                style={{ float: 'right', marginTop: 2 }} />}
            {job_types && <p>{job_types}</p>}
            {job_category && <p>{job_category}</p>}
            {date && <p>{date}</p>}
        </span>
    }

    return '[New]'
}

const vTabStyle = {
    whiteSpace: 'normal',
    maxWidth: '120px',
    minHeight: '50px',
    lineHeight: '20px',
    marginBottom: '10px',
    border: '1px solid'
}

const newButtonStyle = {
    border: '1px dashed'
}

const iconStyle = {
    display: 'flex',
    cursor: 'pointer',
    paddingRight: '2px',
    paddingTop: '4px'
}

const noneStyle = {
    display: 'none'
}

const deleteSubJobButtonStyle = {
    height: '20px',
    width: '20px',
    marginRight: '-15px'
}

const parseAddressArray = (address, lat, lon) => {
    return {
        line_1: `${address.street_number ? `${address.street_number} ` : ''}${address.road}`,
        city: address.city,
        county: address.county,
        state: address.state,
        zip: address.postal_code,
        lat: lat,
        lon: lon
    }
}

const setAddressFields = (setFieldValue, address) => {
    setFieldValue('address_line_1', address.line_1 && address.line_1 !== 'undefined' ? address.line_1 : null)
    setFieldValue('city', address.city && address.city !== 'undefined' ? address.city : null)
    setFieldValue('county', address.county && address.county !== 'undefined' ? address.county : null)
    setFieldValue('state', address.state && address.state !== 'undefined' ? address.state : null)
    setFieldValue('zip_code', address.zip && address.zip !== 'undefined' ? address.zip : null)
    setFieldValue('lat', address.lat && address.lat !== 'undefined' ? address.lat : null)
    setFieldValue('lon', address.lon && address.lon !== 'undefined' ? address.lon : null)
}

const getPebbleSummaryDetails = (subJobs, crews) => {
    const results = []
    if (crews.length === 0) {
        // Optimization for when lookup query not completed yet
        return null
    }

    const getCrewFromSubJob = (subJob, phaseTypeId) => {
        const crewInformation = subJob.pebble_details?.crew_information
        const crewId = crewInformation
            ?.filter(ci => ci?.phase_type?.id === phaseTypeId)
            .map(ci => ci?.crew?.id)
        return crewId[0]
    }

    const getCrewName = (id) => {
        const match = crews.find(c => c.id === id)
        return match ? match.name : '---'
    }

    subJobs.forEach(s => {
        if (s?.job_category?.id === 2 && s?.pebble_details?.color_additions?.length) {
            s.pebble_details.color_additions.forEach(c => {
                const mappedResult = {
                    app_date: s.pebble_details.app_date || '---',
                    detail_date: s.pebble_details.detail_date || '---',
                    color: c.color ? c.color.name : '---',
                    additions: c.additions ? c.additions.map(a => a.name).join(', ') : '---',
                    section: c.pool_section ? c.pool_section.name : '---',
                    crew: getCrewName(getCrewFromSubJob(s, 2)),
                    detail_crew: getCrewName(getCrewFromSubJob(s, 3))
                }
                results.push(mappedResult)
            })
        }
    })
    return results
}

const getDefaultSubTab = (sub_jobs, jobDetailId) => {
    if (!jobDetailId) return DEFAULT_SUB_TAB

    const subJobIndex = sub_jobs?.findIndex(s => parseInt(s.id) === parseInt(jobDetailId))

    if (!subJobIndex || subJobIndex === -1) return DEFAULT_SUB_TAB

    return `sub_jobs-${subJobIndex}`
}

const JobDetailsCard = ({
    dirty,
    values,
    tabState,
    setTabState,
    documentTags,
    lookupOptions,
    setFieldValue,
    onSubJobDelete,
    loadingBaseJob,
    setMainTabState,
    selectedDetailTab,
    onChangeDetailTab,
    defaultJobDetailId,
    documentPresiteTabRefresh,
    setDocumentPresiteTabRefresh,
    ...props
}) => {
    
    const [sidebarVisible, setSidebarState] = useState(true)
    const [deleteSubJobDialogIsOpen, setDeleteSubJobDialogIsOpen] = useState(false)
    const colStyle = !sidebarVisible ? noneStyle : {}

    useEffect(() => {
        if (tabState !== 'sub_jobs-0') return

        if (!values?.loadingBaseJob) {
            const defaultTabState = getDefaultSubTab(values?.sub_jobs, defaultJobDetailId)
            setTabState(defaultTabState)
        }
        // eslint-disable-next-line
    }, [values?.loadingBaseJob, defaultJobDetailId])

    const pebbleSummaryDetails = useMemo(() => {
        return getPebbleSummaryDetails(values?.sub_jobs, lookupOptions.lookupCrewTypes)
    }, [values.sub_jobs, lookupOptions.lookupCrewTypes])

    return <>
        <FieldArray
            name="sub_jobs"
            label="Jobs"
            render={({ push }) => {
                return (<>
                    <Divider />
                    <div style={{ display: 'flex', flexWrap: 'wrap' }}
                        className={loadingBaseJob ? 'bp3-skeleton min-height-50vh' : ''}>
                        {!sidebarVisible && <Icon
                            style={{ cursor: 'pointer', paddingTop: '4px', paddingRight: '4px' }}
                            icon='caret-right'
                            iconSize='20'
                            onClick={() => setSidebarState(true)}
                        />
                        }
                        <div className='job-sidebar-pane' style={colStyle}>

                            <Tabs
                                id="job_detail_tab"
                                onChange={setTabState}
                                selectedTabId={tabState}
                                className={'job-sidebar'}
                                vertical
                            >
                                {values?.was_estimated && <Tab
                                    style={vTabStyle}
                                    id={`sub_jobs-estimate`}
                                    key={`sub_jobs-estimate`}
                                    title={'Estimated'}
                                    disabled={true}
                                />}
                                {values?.sub_jobs?.map((sub_job, idx) => {
                                    return (
                                        <Tab
                                            style={vTabStyle}
                                            id={`sub_jobs-${idx}`}
                                            key={`sub_jobs-${idx}`}
                                            title={_getTabTitle(sub_job)}
                                        >
                                            {sub_job.is_still_loading &&
                                                <Spinner size={24} />
                                            }
                                            {sub_job.is_syncing &&
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        padding: 5
                                                    }}
                                                >
                                                    <Icon icon='refresh' className='spinner' />
                                                </div>
                                            }
                                            {sub_job.has_error &&
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        padding: 5
                                                    }}
                                                >
                                                    <Tooltip
                                                        content={
                                                            <>
                                                                <p>An error occurred that prevented this sub-job
                                                                    from
                                                                    being loaded. Please refresh your page.</p>
                                                                <p>{sub_job.error.message}</p>
                                                            </>
                                                        }
                                                        position={Position.RIGHT}
                                                    >
                                                        <Icon icon="error" intent="danger" iconSize={20} />
                                                    </Tooltip>
                                                </div>
                                            }
                                            {!sub_job.is_still_loading && !sub_job.is_syncing && !sub_job.has_error &&
                                                <CanDeleteJob>
                                                    <span style={{ float: 'right' }}>
                                                        <Button minimal
                                                            icon='trash'
                                                            style={deleteSubJobButtonStyle}
                                                            onClick={() => setDeleteSubJobDialogIsOpen(true)} />
                                                    </span>
                                                </CanDeleteJob>
                                            }
                                        </Tab>
                                    )
                                })}
                                <NavButton style={newButtonStyle} onClick={() => {
                                    push(DEFAULT_JOB_DETAIL_STATE)
                                    const newTabIdx = values?.sub_jobs?.length
                                    setTabState(`sub_jobs-${newTabIdx}`)
                                }}><Icon icon='plus' style={addButtonStyle} />Add New</NavButton>
                            </Tabs >

                            <Icon style={iconStyle} icon='caret-left' iconSize='20'
                                onClick={() => setSidebarState(false)} />

                        </div >
                        <div className='job-detail-pane'>
                            {values?.sub_jobs?.map((sub_job, idx) => {
                                if (sub_job.is_still_loading) {
                                    return <div className='bp3-skeleton min-height-50vh' />
                                } else {
                                    return (
                                        tabState === `sub_jobs-${idx}` &&
                                        <JobDetailForm
                                            key={`sub_jobs.${idx}`}
                                            {...props}
                                            jobId={values.id}
                                            valuePrefix={`sub_jobs.${idx}`}
                                            values={sub_job}
                                            dirty={dirty}
                                            jobTypes={lookupOptions.lookupJobType}
                                            poolTypes={lookupOptions.lookupPoolType}
                                            spaTypes={lookupOptions.lookupSpaType}
                                            jobStatuses={lookupOptions.lookupJobStatus}
                                            jobCategories={lookupOptions.lookupJobCategory}
                                            crews={lookupOptions.lookupCrewTypes}
                                            guniteHistoryTypes={lookupOptions.lookupGuniteHistoryTypes}
                                            employees={lookupOptions.lookupEmployee.sort((a, b) => { return a.name < b.name ? -1 : a.name > b.name ? 1 : 0 })}
                                            pebbleDetailJobTypes={lookupOptions.lookupPebbleJobType}
                                            poolSections={lookupOptions.lookupPoolSection}
                                            pebbleColors={lookupOptions.lookupPebbleColor}
                                            pebbleColorMaterialTypes={lookupOptions.lookupPebbleColorMaterialType}
                                            pebbleColorAdditives={lookupOptions.lookupPebbleColorAdditive}
                                            additions={lookupOptions.lookupAddition}
                                            warrantyCategories={lookupOptions.lookupWarrantyCategory}
                                            jobPriorities={lookupOptions.lookupJobPriority}
                                            jobDescriptions={lookupOptions.jobDescriptionJobType}
                                            pebbleSummaryDetails={pebbleSummaryDetails}
                                            vgbMainDrainsBy={lookupOptions.lookupVgbMainDrainsBy}
                                            slurryTypes={lookupOptions.lookupSlurryType.filter(s => s.type === 'Presite')}
                                            repairSupervisors={lookupOptions.lookupRepairSupervisor}
                                            setMainTabState={setMainTabState}
                                            documentPresiteTabRefresh={documentPresiteTabRefresh}
                                            setDocumentPresiteTabRefresh={setDocumentPresiteTabRefresh}
                                            setFieldValue={setFieldValue}
                                            pebblePhases={lookupOptions.lookupPebblePhaseType}
                                            jobOrders={lookupOptions.lookupJobOrder}
                                            presiteSupervisors={lookupOptions.presiteSupervisors}
                                            documentTags={documentTags}
                                            companyDivisions={lookupOptions.lookupCompanyDivisions}
                                            slurryPickupOptions={lookupOptions.lookupSlurryPickup.filter(s => s.is_active)}

                                            job={values}
                                            subJobIndex={idx}
                                            deleteSubJobDialogIsOpen={deleteSubJobDialogIsOpen}
                                            setDeleteSubJobDialogIsOpen={setDeleteSubJobDialogIsOpen}
                                            onSubJobDelete={onSubJobDelete}
                                        />
                                    )
                                }
                            }
                            )}
                        </div>
                    </div >
                </>
                )
            }
            }
        />
    </>
}

const DEFAULT_TAB = 'job_info_tab'
const DEFAULT_DETAIL_TAB = 'detail_pebble_tab'

export default ({ values = {}, dirty, lookupOptions = {}, supervisors, homeowners, defaultJobDetailId, loadingBaseJob,
    hardRefreshDocuments, setHardRefreshDocuments, isSubmitting, documentTags, initialValues = {},
    lookupLoading = false, ...props }) => {
    const location = useLocation()
    //this will be set to true if they came from the job creation page but that state remains
    //on the location if they arrive here through refresh or forward/back browser buttons
    const isDirectlyFromJobCreationPage = location?.state?.fromJobCreationPage

    const criticalNotesData = values?.sub_jobs.filter(sj => sj.is_critical_note && sj.critical_note?.trim()).map(jd => convertJobDetailToCriticalNoteData(jd))
    const showCriticalNote = !isDirectlyFromJobCreationPage && criticalNotesData?.length > 0

    const [showCriticalNoteDialog, setShowCriticalNoteDialog] = useState(showCriticalNote)

    const [tabState, setTabState] = useState(defaultJobDetailId ? 'job_detail_tab' : DEFAULT_TAB)
    const [detailTabState, setDetailTabState] = useState(DEFAULT_DETAIL_TAB)
    const [selectedDetailTabId, setSelectedDetailTabId] = useState('sub_jobs-0')

    const [deleteDialogIsOpen, setDeleteDialogIsOpen] = useState(false)
    const [documentPresiteTabRefresh, setDocumentPresiteTabRefresh] = useState({
        presiteDefaultTab: false,
        documents: [],
        documentsUpdated: false,
        isInitialLoad: true,
        defaultFolder: null,
        key: 'default'
    })

    const setNewOrExistingJobBreadcrumbItem = (values) => {
        return values?.id ? `${values?.name} (${values?.id})` : 'New Job'
    }

    const [addressDialogIsOpen, setAddressDialogIsOpen] = useState(false)

    const [isAddressValidated, setIsAddressValidated] = useState(null)
    const [lastAddress, setLastAddress] = useState(null)

    const canViewJobs = useHasRight(CAN_VIEW_JOB)

    useEffect(() => {
        setLastAddress({
            line_1: values?.address_line_1,
            city: values?.city,
            county: values?.county,
            state: values?.state,
            zip: values?.zip_code,
            lat: values?.lat,
            lon: values?.lon
        })
        // eslint-disable-next-line
    }, [values?.loadingBaseJob, setLastAddress])

    useEffect(() => {
        const isValidated = (values && initialValues && lastAddress &&
            lastAddress.line_1 === values.address_line_1 &&
            lastAddress.city === values.city &&
            lastAddress.county === values.county &&
            lastAddress.state === values.state &&
            (lastAddress.zip === values.zip_code || (!lastAddress.zip && !values.zip_code)) &&
            !(values.lat === null || values.lat === undefined) &&
            !(values.lon === null || values.lon === undefined)) &&
            lastAddress.lat && lastAddress.lat.toString() === values.lat.toString() &&
            lastAddress.lon && lastAddress.lon.toString() === values.lon.toString()
        setIsAddressValidated(isValidated)
        // eslint-disable-next-line
    }, [values?.address_line_1, values?.city, values?.state, values?.zip_code, values?.county, lastAddress])

    useEffect(() => {
        if (values && isAddressValidated !== values.isValidAddress) {
            props.setFieldValue('isValidAddress', isAddressValidated)
        }
        // eslint-disable-next-line
    }, [isAddressValidated, values?.isValidAddress])

    useEffect(() => {
        setShowCriticalNoteDialog(showCriticalNote)
        // eslint-disable-next-line
    }, [values?.loadingBaseJob, setShowCriticalNoteDialog, showCriticalNote])

    const selectAddress = (address) => {
        if (!address) return
        const addressObject = parseAddressArray(address.address, address.lat, address.lon)
        setAddressFields(props.setFieldValue, addressObject)
        setLastAddress(addressObject)
    }

    const mappedSupervisors = supervisors.map(c => ({
        ...c,
        name: `${c.first_name || ''} ${c.last_name || ''}` // eslint-disable-next-line
    })).filter(e => values && values.company && e.customer_id == values.company.id)

    const mappedHomeowners = homeowners.map(c => ({
        ...c,
        name: `${c.first_name || ''} ${c.last_name || ''}`
    }))

    const isMobile = useMediaQuery({ query: '(max-width: 800px)' })
    const selectedJobDetail = values?.sub_jobs?.[selectedDetailTabId.split('-')[1]]
    const subJobsAreLoading = !values || !values.sub_jobs || values.sub_jobs.some(sj => sj.is_still_loading)
    const subJobsAreSyncing = !values || !values.sub_jobs || values.sub_jobs.some(sj => sj.is_syncing)
    const subJobError = !values || !values.sub_jobs || values.sub_jobs.some(sj => sj.has_error)

    const formModified = !isSubmitting && values && initialValues && isChanged({
        ...initialValues,
        isValidAddress: values?.isValidAddress
    }, values)

    return (
        <Form>
            {values &&
                <>
                    <Prompt
                        when={formModified}
                        message={'You have unsaved changes. Navigate away?'}
                    />

                    <DeleteJobDialog
                        deleteDialogIsOpen={deleteDialogIsOpen}
                        setDeleteDialogIsOpen={setDeleteDialogIsOpen}
                        onDelete={props.onDelete}
                    />

                    <CheckAddressDialog
                        values={values}
                        isOpen={addressDialogIsOpen}
                        save={(evt) => selectAddress(evt)}
                        cancel={() => setAddressDialogIsOpen(false)}
                        setIsOpen={(newValue) => setAddressDialogIsOpen(newValue)}
                        enableCheckForDuplicates={!values?.id} //enable for jobCreation, not JobEdit
                    />
                </>
            }
            <CardWithBreadcrumb items={[{
                text: 'Job Search',
                to: canViewJobs ? '/job' : '',
                icon: 'briefcase'
            }, { text: setNewOrExistingJobBreadcrumbItem(values), isLoading: loadingBaseJob }]} isMobile={isMobile}>
                <ResetButton onClick={props.onCancel} isMobile={isMobile} />
                <SaveButton isMobile={isMobile}
                    disabled={subJobsAreLoading || subJobsAreSyncing || isSubmitting || subJobError || lookupLoading}
                />
                {values?.id &&
                    <CanDeleteJob>
                        <DeleteButton onClick={() => setDeleteDialogIsOpen(true)} isMobile={isMobile} textOverride='Delete Job' />
                    </CanDeleteJob>
                }
            </CardWithBreadcrumb>

            {showCriticalNoteDialog &&
                <CriticalNoteDialog
                    isOpen={showCriticalNoteDialog}
                    criticalNotesData={criticalNotesData}
                    onClose={() => setShowCriticalNoteDialog(false)}
                />
            }

            <Card style={{
                minWidth: 300,
                position: 'relative'
            }}>
                <Tabs
                    id='job_form_tabs'
                    onChange={setTabState}
                    selectedTabId={tabState}
                    className='job-form-tabs'
                >
                    <Tab
                        id="job_info_tab"
                        title={<span> Job Info
                            {loadingBaseJob && <TabSpinner />}
                        </span>
                        }
                        panelClassName=''
                        panel={<JobInfoCard
                            dirty={dirty}
                            values={values}
                            loadingBaseJob={loadingBaseJob}
                            homeowners={mappedHomeowners}
                            supervisors={mappedSupervisors}
                            estimates={lookupOptions.estimates}
                            onCheckAddressClicked={setAddressDialogIsOpen}
                            {...props}
                        />}
                    />
                    <Tab
                        id="job_detail_tab"
                        title={<span> Job Details
                            {subJobsAreLoading && <TabSpinner />}
                            {subJobsAreSyncing && <TabSyncing />}
                        </span>
                        }
                        panel={<JobDetailsCard
                            {...props}
                            dirty={dirty}
                            values={values}
                            documentTags={documentTags}
                            lookupOptions={lookupOptions}
                            setMainTabState={setTabState}
                            loadingBaseJob={loadingBaseJob}
                            selectedDetailTab={detailTabState}
                            setFieldValue={props.setFieldValue}
                            onChangeDetailTab={setDetailTabState}
                            defaultJobDetailId={defaultJobDetailId}
                            documentPresiteTabRefresh={documentPresiteTabRefresh}
                            setDocumentPresiteTabRefresh={setDocumentPresiteTabRefresh}
                            tabState={selectedDetailTabId}
                            setTabState={setSelectedDetailTabId}
                        />}
                    />
                    <Tab
                        id="document_tab"
                        title="Documents"
                        panel=
                        {loadingBaseJob || subJobsAreLoading
                            ? <NonIdealState
                                icon='download'
                                title={'Almost there...'}
                                description={'Please wait while we load necessary subrecords.'}
                            />
                            : values && <DocumentTree
                                values={values}
                                isEstimate={false}
                                documentTags={documentTags}
                                innerKey={documentPresiteTabRefresh.key}
                                openFolder={documentPresiteTabRefresh.openFolder}
                            />
                        }
                        disabled={(values?.id == null || values.id === 0) && !loadingBaseJob}
                    />
                </Tabs>
                <CanSendBuilderEmail>
                    <div style={{
                        position: "absolute",
                        right: 25,
                        top: 25
                    }}>
                        <BuilderEmail
                            formModified={formModified}
                            disabled={loadingBaseJob}
                            jobDetailId={selectedJobDetail?.id}
                            jobId={values?.id}
                            contactEmail={values?.company?.schedule_contact_email}
                            supervisorEmail={values?.company_supervisor?.email_address}
                            jobDetailType={selectedJobDetail?.job_category?.id}
                            pebblePhaseTypes={selectedJobDetail?.pebble_details?.crew_information?.map(crew => crew.phase_type?.id)}
                            large
                        />
                    </div>
                </CanSendBuilderEmail>
            </Card>
        </Form>
    )
}

const TabSpinner = () => <Spinner style={{ display: 'inline-block', marginLeft: '.5em' }} size={16} />
const TabSyncing = () => <Icon style={{ display: 'inline-block', marginLeft: '.5em' }} size={16} icon='refresh' className='spinner' />
